import { AbstractWsConnector, WsConnectionStatus } from '@/ws/types';

export type WsGroupInvocation = [eventName: string, ...args: unknown[]];

/**
 * Ref-counted membership of one server-side notification group.
 *
 * Two things make this worth sharing between notifiers:
 *
 * - Joining and leaving a group affects the **whole connection**, not an individual listener. Without
 *   ref-counting, one component unmounting would stop delivery for every other component still
 *   listening, so membership is acquired on the first subscriber and released only by the last.
 * - SignalR groups live for the lifetime of a connection, and the worker only replays invocations
 *   made *while* disconnected. A group joined on a healthy connection is therefore never re-joined
 *   after a reconnect, and the client goes silently deaf. Watching the connection status and
 *   re-joining on the way back to `connected` closes that gap; the hub's group join is idempotent,
 *   so re-joining is safe even when the worker happens to replay the invocation too.
 *
 * This models a single fixed group. A notifier that needs many independent memberships (joining
 * per-entity groups, say) wants a keyed registry of these rather than one instance.
 */
export class WsGroupSubscription {
  private subscriberCount = 0;

  private stopWatchingStatus?: () => void;

  private lastStatus?: WsConnectionStatus;

  constructor(
    private readonly connector: AbstractWsConnector,
    private readonly joinInvocation: WsGroupInvocation,
    private readonly leaveInvocation: WsGroupInvocation,
  ) {}

  /** Joins the group if this is the first holder. Returns an idempotent release. */
  acquire() {
    this.subscriberCount += 1;
    if (this.subscriberCount === 1) this.start();

    let released = false;
    return () => {
      // React StrictMode invokes effect cleanups twice; releasing once keeps the count honest.
      if (released) return;
      released = true;

      this.subscriberCount -= 1;
      if (this.subscriberCount === 0) this.stop();
    };
  }

  private join() {
    this.connector.invoke(...this.joinInvocation);
  }

  private start() {
    this.join();
    // Seeded from the current status so only a transition *into* `connected` re-joins — never the
    // initial render, and never a status event that leaves the connection already up.
    this.lastStatus = this.connector.getStatus();
    this.stopWatchingStatus = this.connector.onStatusChange((status) => {
      if (status === 'connected' && this.lastStatus !== 'connected') this.join();
      this.lastStatus = status;
    });
  }

  private stop() {
    this.stopWatchingStatus?.();
    this.stopWatchingStatus = undefined;
    this.lastStatus = undefined;
    this.connector.invoke(...this.leaveInvocation);
  }
}
